import React, { useEffect, useState } from "react";
import { PlayCircle, Plus, Share2, Trash2, Film, Link2, Dumbbell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AddVideoModal from "@/components/videos/AddVideoModal";
import AssignVideoModal from "@/components/videos/AssignVideoModal";
import VideoPlayerModal from "@/components/videos/VideoPlayerModal";

const CATEGORY_LABELS = {
  technique: "Technique",
  warmup: "Warm-up",
  mobility: "Mobility",
  program: "Program",
  nutrition: "Nutrition",
  other: "Other",
};

export default function Videos() {
  const { user } = useAuth();
  const isCoach = Boolean(user?.is_coach || user?.is_admin);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [playing, setPlaying] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/videos");
      setVideos(r.data.videos || []);
    } catch {
      toast.error("Could not load the video library");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}" from the library? Clients will lose access.`)) return;
    try {
      await api.delete(`/videos/${v.id}`);
      toast.success("Video deleted");
      setVideos((vs) => vs.filter((x) => x.id !== v.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const linkExercise = async (v) => {
    const name = window.prompt(
      'Link this video to an exercise name (clients see a cue-video button when logging it). Leave empty to unlink.',
      v.exercise_name || "",
    );
    if (name === null) return;
    try {
      const r = await api.put(`/videos/${v.id}`, { exercise_name: name });
      setVideos((vs) => vs.map((x) => (x.id === v.id ? { ...x, exercise_name: r.data.exercise_name } : x)));
      toast.success(r.data.exercise_name ? `Linked to "${r.data.exercise_name}"` : "Exercise link removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update video");
    }
  };

  const cats = ["all", ...new Set(videos.map((v) => v.category || "other"))];
  const shown = filter === "all" ? videos : videos.filter((v) => (v.category || "other") === filter);

  return (
    <div data-testid="videos-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="overline text-blaze">/ Video library</div>
          <h1 className="font-heading text-5xl uppercase mt-1">Watch. Learn. Lift.</h1>
          <p className="text-sm text-zinc-400 mt-2">
            {isCoach
              ? "Build your coaching library and share videos with specific clients or your whole roster."
              : "Videos your coach shared with you."}
          </p>
        </div>
        {isCoach && (
          <button
            type="button"
            data-testid="add-video-btn"
            onClick={() => setShowAdd(true)}
            className="blaze-btn px-5 py-2.5 text-xs inline-flex items-center gap-2"
          >
            <Plus className="h-3.5 w-3.5" /> Add video
          </button>
        )}
      </div>

      {cats.length > 2 && (
        <div className="flex gap-2 mb-5 flex-wrap">
          {cats.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`video-filter-${c}`}
              onClick={() => setFilter(c)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest rounded-sm border transition-colors ${filter === c ? "border-blaze text-blaze bg-blaze/10" : "border-obsidian-600 text-zinc-400 hover:text-zinc-200"}`}
            >
              {c === "all" ? "All" : CATEGORY_LABELS[c] || c}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm uppercase tracking-widest">Loading library…</div>
      ) : shown.length === 0 ? (
        <div className="obs-card text-center py-14" data-testid="videos-empty">
          <Film className="h-8 w-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">
            {isCoach
              ? "No videos yet — add your first technique breakdown or program walkthrough."
              : "Nothing here yet. Your coach hasn't shared any videos with you."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((v) => (
            <div key={v.id} className="obs-card p-0 overflow-hidden flex flex-col" data-testid={`video-card-${v.id}`}>
              <button
                type="button"
                data-testid={`video-play-${v.id}`}
                onClick={() => setPlaying(v)}
                className="relative aspect-video bg-obsidian-900 grid place-items-center group"
              >
                <PlayCircle className="h-12 w-12 text-zinc-600 group-hover:text-blaze transition-colors" />
                <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest px-2 py-0.5 bg-obsidian-800/90 border border-obsidian-600 rounded-sm text-zinc-300">
                  {CATEGORY_LABELS[v.category] || "Other"}
                </span>
                {v.source === "link" && (
                  <span className="absolute top-2 right-2 text-zinc-500">
                    <Link2 className="h-3.5 w-3.5" />
                  </span>
                )}
              </button>
              <div className="p-3 flex-1 flex flex-col">
                <div className="font-heading text-lg uppercase leading-tight">{v.title}</div>
                {v.description && (
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{v.description}</p>
                )}
                {v.exercise_name && (
                  <span className="inline-flex items-center gap-1.5 mt-2 self-start text-[10px] uppercase tracking-widest px-2 py-0.5 border border-blaze/30 text-blaze/90 rounded-sm" data-testid={`video-exercise-chip-${v.id}`}>
                    <Dumbbell className="h-3 w-3" /> {v.exercise_name}
                  </span>
                )}
                <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {isCoach
                      ? v.share_roster
                        ? "Shared: whole roster"
                        : `Shared: ${(v.assigned_client_ids || []).length} client${(v.assigned_client_ids || []).length === 1 ? "" : "s"}`
                      : `From ${v.coach_name || "your coach"}`}
                  </span>
                  {isCoach && (
                    <span className="flex gap-1">
                      <button
                        type="button"
                        data-testid={`video-link-exercise-${v.id}`}
                        onClick={() => linkExercise(v)}
                        className="p-1.5 text-zinc-500 hover:text-blaze"
                        title="Link to an exercise"
                      >
                        <Dumbbell className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        data-testid={`video-share-${v.id}`}
                        onClick={() => setAssigning(v)}
                        className="p-1.5 text-zinc-500 hover:text-blaze"
                        title="Share with clients"
                      >
                        <Share2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        data-testid={`video-delete-${v.id}`}
                        onClick={() => remove(v)}
                        className="p-1.5 text-zinc-500 hover:text-blaze"
                        title="Delete video"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddVideoModal
          onClose={() => setShowAdd(false)}
          onCreated={(v) => {
            setShowAdd(false);
            setVideos((vs) => [v, ...vs]);
            setAssigning(v); // straight into sharing
          }}
        />
      )}
      {assigning && (
        <AssignVideoModal
          video={assigning}
          onClose={() => setAssigning(null)}
          onSaved={(res) => {
            setVideos((vs) =>
              vs.map((x) =>
                x.id === assigning.id
                  ? { ...x, assigned_client_ids: res.assigned_client_ids, share_roster: res.share_roster }
                  : x,
              ),
            );
            setAssigning(null);
          }}
        />
      )}
      {playing && <VideoPlayerModal video={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}
